/*
* Description: Crypto API endpoints for sagas
* Author:      PreVeil, LLC
*/
import { CreateUserData, IFetchResponse, LocalUser, Signature, GetThreadsParams } from "@preveil-api";
import {
  DeleteEmailsApiArg, DeleteEmailsApiResponse, SetEmailFlagApiArg, GetPaginatedThreadsRemoteApiResponse, SetEmailFlagApiResponse,
  MoveEmailsToMailboxApiArg
} from "./crypto-rtk-api";
import { Account, AccountIdentifiers, Helpers, BaseAPI, parseGetAppThreads, ConfigKeys, isSameUser, KeyFactory, AppUserKey, pvConsoleLogger } from "src/common";
import { DynamicQueue } from "../base";
import { getConfigValue } from "src/store";
// const base_url = `${process.env.REACT_APP_CRYPTO_SERVER}`;
// --------------------------------------------------------------------------------------------
// Saga calls
// NOTE: base_url needs to be filled dynamically from store state App > Config
// --------------------------------------------------------------------------------------------
/*
 * Description: set App Config from env variables ***** Working *****
 * Request: GET; no params
 * Response: { ... }
 */
const getAppConfiguration = (): Promise<IFetchResponse> => {
  return BaseAPI.get(`${getConfigValue(ConfigKeys.crypto_server)}/get/buildversion`);
}

/*
* Description: Verify Crypto Status (legacy: verifyCryptoStatus)
* Request: GET no params
* Response: {"cs_connection": boolean}
*/
const getCryptoStatus = (): Promise<IFetchResponse> => {
  return BaseAPI.get(`${getConfigValue(ConfigKeys.crypto_server)}/ping`);
}

/*
 * Description: Gets a list of local accounts from crypto
 * Request: Optional param user_id
 * Response: if !!user_id = Sends user_id to crypto for misc and gets a list of local accounts / users from crypto
 *           else returns an array of users - users: Array<LocalUser>
 * Notes: Parse LocalUser data to create a local user - return type IFetchResponse
 *        previously getLocalUsers = gets a list of local user accounts
 *        optional call to {crypto}/updateAndListLocalUsers
 */
const getLocalAccounts = async (user_id?: string): Promise<IFetchResponse> => {
  const base_url = getConfigValue(ConfigKeys.crypto_server);
  const url = !!user_id ? `${base_url}/get/users/${encodeURIComponent(user_id)}/localusers` :
    `${base_url}/get/account/list/local`;
  const user_key = !!user_id ? await getUserKey(user_id) : undefined;
  return BaseAPI.put(url)
    .then(async (response: IFetchResponse) => {
      if (response.isError) { return response; }
      const users: LocalUser[] = response.data.users;
      return (!!users && Array.isArray(users) && users.length > 0) ?
        {
          ...response, data: {
            users: await Promise.all(users.map(async (user) => {
              const _user_key = isSameUser(user.user_id, user_id) && !!user_key ? user_key : undefined;
              return await Account.initAccount(user, _user_key);
            }))
          }
        } : response;
    });
}

// Description: Gets a list of local accounts from crypto
const getUserKey = (user_id: string): Promise<AppUserKey | undefined> => {
  const url = `${getConfigValue(ConfigKeys.crypto_server)}/get/account/${encodeURIComponent(user_id)}/key/force_get`;
  return BaseAPI.put(url).then(async (response: IFetchResponse) => {
    try {
      const decoded_user_key = Helpers.b64Decode(response.data.key);
      const key = KeyFactory.deserializeUserKey(decoded_user_key);
      return key;
    } catch (error) {
      console.error("Error attempting to get the AppUserKey from crypto: ", error);
    }
  });
}
/*
 * Description: Sets up a new local account in crypto
 * Request: user_id: string; secret: string; key_version: string; display_name?: string
 * Response: returns an array of users - users: Array<LocalUser>
 * Notes: Parse LocalUser (JSONLocalUser) data to create a local user - return type IFetchResponse
 *        previously getLocalUsers = gets a list of local user accounts
 *        optional call to {crypto}/updateAndListLocalUsers
 */
const setupNewAccount = (createUserData: CreateUserData): Promise<IFetchResponse> => {
  const url = `${getConfigValue(ConfigKeys.crypto_server)}/put/account/${encodeURIComponent(createUserData.user_id)}`;
  return BaseAPI.put(url, {
    secret: createUserData.secret,
    key_version: createUserData.key_version,
    display_name: createUserData.display_name
  })
    .then(async (response: IFetchResponse) => {
      if (response.isError) { return response; }
      const user: LocalUser = response.data;
      return !!user ? { ...response, data: { current_account: await Account.initAccount(user) } } : response;
    });
}

/*
 * Description: Upgrade an existing user account to crypto from new express Accounts
 * Request: UpgradeExpressAccountApiArg
 * Response: UpgradeExpressAccountApiResponse
 */
const upgradeExpressAccount = (account_ids: AccountIdentifiers): Promise<IFetchResponse> => {
  const url = `${getConfigValue(ConfigKeys.crypto_server)}/account/${encodeURIComponent(account_ids.user_id)}/upgrade`;
  return BaseAPI.put(url, {
    device_id: account_ids.device_id,
    user_key: account_ids.user_key_serialized,
    device_key: account_ids.device_key_serialized
  });
}

/*
* Description: Sign with User Key in Crypto
* Request: message to sign 
* Response: SignWithUserKeyApiResponse =>  {key_version: number; signature: string;}
*/
const signWithUserKey = (message: Uint8Array, user_id: string, key_version: number = -1): Promise<Signature> => {
  return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}/post/${user_id}/sign`, { plaintext: Helpers.b64Encode(message) })
    .then(response => {
      return { signature: Helpers.b64Decode(response.data.signature) } as Signature;
    });
}

/*
* Description: Sign with Device Key in Crypto
* Request: message to sign
* Response: SignWithDeviceKeyApiResponse =>  {key_version: number; signature: string;}
*/
const signWithDeviceKey = (message: Uint8Array, user_id: string): Promise<Signature> => {
  return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}/post/${user_id}/device/sign`, { plaintext: Helpers.b64Encode(message) })
    .then(response => {
      // NOTE: key_version is required from the crypto server (see the device/sign route). If it is ever missing the
      // caller falls back to the in-memory version, which can be stale — log it so a contract regression is visible.
      (response.data.key_version === undefined || response.data.key_version === null) &&
        pvConsoleLogger("signWithDeviceKey: crypto server response missing key_version — device key version may be stale", false);
      return { signature: Helpers.b64Decode(response.data.signature), key_version: response.data.key_version } as Signature;
    });
}

/*
* Description: decrypt with Crypto - Use the queue system because of the amount of calls
* Request: cipher to decrypt
* Response:  {plaintext: Uint8Array;}
*/
const queue = new DynamicQueue();
function decrypt(cipher: Uint8Array, user_id: string, key_version: number = -1): Promise<Uint8Array> {
  return queue.addRequest(BaseAPI.put, {
    url: `${getConfigValue(ConfigKeys.crypto_server)}/post/${user_id}/decrypt`,
    data: { ciphertext: Helpers.b64Encode(cipher), decrypt_key_version: key_version }
  }).then(response => { // NOTE: need to return Promise<Uint8Array>
    return (typeof response.data.plaintext === "string") ? Helpers.b64Decode(response.data.plaintext) :
      response.data.plaintext as Uint8Array;
  });
}
// const decrypt = (cipher: Uint8Array, user_id: string, key_version: number = -1): Promise<Uint8Array> => {
//   return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}/post/${user_id}/decrypt`, { ciphertext: Helpers.b64Encode(cipher), decrypt_key_version: key_version })
//     .then(response => { // NOTE: need to return Promise<Uint8Array>
//       return (typeof response.data.plaintext === "string") ? Helpers.b64Decode(response.data.plaintext) :
//         response.data.plaintext as Uint8Array;
//     });
// }

/*
* Description: encrypt with Crypto
* Request:  
    encrypt_for_user_id = j.get("encrypt_for"),
    encrypt_for_key_version = j.get("encrypt_for_key_version", -1)
    plaintext = j.get("plaintext")
* Response:  { 
    "ciphertext" : b64enc(ciphertext),
    "encrypt_key_version" : encrypt_for.public_user_key.key_version,: string;
*/
const encrypt = (plaintext: Uint8Array, user_id: string, key_version: number = -1): Promise<Uint8Array> => {
  return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}/post/${user_id}/encrypt`, {
    encrypt_for: user_id,
    encrypt_for_key_version: key_version,
    plaintext: Helpers.b64Encode(plaintext)
  })
    .then(response => { return response.data.ciphertext as Uint8Array });
}

/*
* Description:  For Developers to test device key rotation
*/
const triggerDeviceKeyRotation = (user_id: string): Promise<IFetchResponse> => {
  return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}/user/${user_id}/device/refresh`);
}

/*
* Description:   GET Mail Paginated threads by collID and MailboxId
* Returns:       Process returned data int UI models
*/
const getPaginatedThreadsRemote = (params: GetThreadsParams): Promise<IFetchResponse> => {
  const url = `/get/mail/${encodeURIComponent(params.user_id)}/mailboxes/${params.mailbox_id}/threads`;
  return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}${url}`, { limit: params.limit, offset: params.offset })
    .then((response: IFetchResponse) => {
      return (!response.isError && !!response.data) ? {
        ...response,
        data: parseGetAppThreads(response.data as GetPaginatedThreadsRemoteApiResponse, params.mailbox_id)
      } : response;
    })
}

/*
* Description: GET Mail Paginated threads by collID and MailboxId (deprecated only used as backup)
* Returns:       Process returned data int UI models
*/
const getPaginatedThreads = (params: GetThreadsParams): Promise<IFetchResponse> => {
  const mailboxData = [{
    id: params.mailbox_id,
    limit: params.limit,
    offset: params.offset
  }];
  const url = `/mail/${encodeURIComponent(params.user_id)}`;
  return BaseAPI.put(`${getConfigValue(ConfigKeys.crypto_server)}${url}`, mailboxData)
    .then((response: IFetchResponse) => {
      return (!response.isError && !!response.data) ? {
        ...response,
        data: parseGetAppThreads(response.data as GetPaginatedThreadsRemoteApiResponse, params.mailbox_id)
      } : response;
    });
}

/*
* Description: Update Flags through crypto
* Returns:      SetEmailFlagApiResponse
*/
const updateFlags = (user_id: string, updates: SetEmailFlagApiArg, remove: boolean = false): Promise<IFetchResponse> => {
  const url = remove ? `${getConfigValue(ConfigKeys.crypto_server)}/delete/account/${encodeURIComponent(user_id)}/emails/flag` :
    `${getConfigValue(ConfigKeys.crypto_server)}/put/account/${encodeURIComponent(user_id)}/emails/flag`;
  return BaseAPI.put(url, updates)
    .then((response: IFetchResponse) => ((!response.isError && !!response.data) ? { ...response, data: response.data as SetEmailFlagApiResponse } : response))
}

/*
* Description:  Delete mail threads
* Returns:      DeleteEmailsApiResponse
*/
const deleteMailThreads = (params: DeleteEmailsApiArg): Promise<IFetchResponse> => {
  const url = `${getConfigValue(ConfigKeys.crypto_server)}/delete/account/${encodeURIComponent(params.userId)}/emails/`;
  return BaseAPI.put(url, params)
    .then((response: IFetchResponse) => ((!response.isError && !!response.data) ?
      { ...response, data: response.data as DeleteEmailsApiResponse } : response))
}

/*
* Description:  Move mail threads
* Returns:      DeleteEmailsApiResponse
*/
const moveMailThreads = (params: MoveEmailsToMailboxApiArg): Promise<IFetchResponse> => {
  const url = `${getConfigValue(ConfigKeys.crypto_server)}/post/account/${encodeURIComponent(params.userId)}/copy`;
  return BaseAPI.put(url, {
    email_ids: params.email_ids,
    dest_mailbox_id: params.dest_mailbox_id,
    src_mailbox_id: params.src_mailbox_id,
    trash_source: true
  })
    .then((response: IFetchResponse) => ((!response.isError && !!response.data) ?
      { ...response, data: response.data as DeleteEmailsApiResponse } : response))
}

export const CryptoAPI = {
  getCryptoStatus,
  getLocalAccounts,
  getAppConfiguration,
  setupNewAccount,
  upgradeExpressAccount,
  signWithUserKey,
  signWithDeviceKey,
  decrypt,
  encrypt,
  triggerDeviceKeyRotation,
  getPaginatedThreadsRemote,
  getPaginatedThreads,
  updateFlags,
  deleteMailThreads,
  moveMailThreads
}
